import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api } from "../../services/api";
import { loginByGoogle } from "../../services/google-auth";
import { useAuth } from "../../store/AuthContext";
import { ErrorNotice } from "../../components/common/Primitives";
import pentabellBlue from "../../assets/brand/pentabell-blue-safe.png";
import pentabellWhite from "../../assets/brand/pentabell-white-safe.png";

export default function AuthenticationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={location.state?.oldPath || "/"} replace />;
  async function signIn() {
    setBusy(true);
    setError("");
    try {
      const config = await api<{ enabled: boolean }>("/auth/google/config");
      if (!config.enabled) throw new Error("Google sign-in is currently unavailable. Please contact your administrator.");
      loginByGoogle(location.state?.oldPath);
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(false);
    }
  }
  return (
    <div className="auth-shell">
      <section className="auth-story">
        <Link to="/login" className="brand">
          <span className="brand-icon">
            <img className="brand-logo brand-logo-light" src={pentabellBlue} alt="" />
            <img className="brand-logo brand-logo-dark" src={pentabellWhite} alt="" />
          </span>
          <span className="brand-copy">
            {t("PentaOffice")}
            <small>{t("BY PENTABELL")}</small>
          </span>
        </Link>
        <div className="auth-story-copy">
          <div className="banner-kicker">
            {t("PEOPLE. WORKPLACE. POSSIBILITIES.")}
          </div>
          <h1>
            {t("Good workdays")}
            <br />
            {t("start here")}
            <span>.</span>
          </h1>
          <p>
            {t(
              "A thoughtful space for the people and little details that keep your workplace moving.",
            )}
          </p>
          <div className="auth-illustration">
            <div className="auth-orbit" />
            <div className="auth-orbit second" />
            <div className="auth-floating">
              {t("A warm welcome.")}
              <br />
              <strong>{t("A connected team.")}</strong>
              <span>✦</span>
            </div>
          </div>
        </div>
        <p className="auth-copyright">
          © {new Date().getFullYear()}{" "}
          {t("PentaOffice")}
        </p>
      </section>
      <section className="auth-form-area">
        <div className="auth-form-container">
          <span className="auth-eyebrow">{t("PentaOffice workspace")}</span>
          <h2>{t("Welcome back")}</h2>
          <p>{t("Use the Google account registered for your PentaOffice account.")}</p>
          {error && <ErrorNotice message={t(error)} />}
          {params.has("error") && <ErrorNotice message={t(params.get("error") || "Google sign-in failed. Please try again.")} />}
          <button type="button" className="button primary auth-submit google-signin" disabled={busy} onClick={signIn}>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.4-.2-2.1H12v4h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.8 3-4.3 3-7.4Z" />
              <path fill="#34A853" d="M12 22c2.7 0 5-1 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.9-1.8-5.7-4.2H3v2.6A10 10 0 0 0 12 22Z" />
              <path fill="#FBBC05" d="M6.3 13.9a6 6 0 0 1 0-3.8V7.5H3a10 10 0 0 0 0 9l3.3-2.6Z" />
              <path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A9.6 9.6 0 0 0 12 2a10 10 0 0 0-9 5.5l3.3 2.6A6 6 0 0 1 12 5.9Z" />
            </svg>
            {t(busy ? "Please wait…" : "Sign in with Google")}
          </button>
          <div className="auth-trust">
            <ShieldCheck size={16} />
            {t("Your workspace. Secure and connected.")}
          </div>
        </div>
      </section>
    </div>
  );
}
